import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import BrandShell from '../components/BrandShell.jsx'
import PointsCounter from '../components/PointsCounter.jsx'
import StreakFlame from '../components/StreakFlame.jsx'
import CapsRing from '../components/CapsRing.jsx'
import BadgeShelf from '../components/BadgeShelf.jsx'
import Sparkline from '../components/Sparkline.jsx'
import HelpTooltip from '../components/HelpTooltip.jsx'
import { useParticipant } from '../hooks/useParticipant.js'
import { usePoints } from '../hooks/usePoints.js'
import { useRealtimeLeaderboard } from '../hooks/useRealtimeLeaderboard.js'
import { weeklyCapsRemaining, totalPoints, pointsByDay } from '../lib/scoring.js'
import { relativeTime } from '../lib/dateUtils.js'

export default function Profile() {
  const { participant } = useParticipant()
  const { activities, events } = usePoints(participant?.id)
  const { rows } = useRealtimeLeaderboard()

  const myRank = rows.findIndex(r => r.participant.id === participant?.id) + 1
  const total = totalPoints(events)
  const caps = useMemo(() => weeklyCapsRemaining(activities), [activities])
  const series = useMemo(() => pointsByDay(events), [events])

  if (!participant) return null

  return (
    <BrandShell>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <header className="flex items-start gap-4">
          <Avatar participant={participant} />
          <div>
            <h1 className="text-2xl font-bold text-brand-text">{participant.name}</h1>
            <p className="text-sm text-brand-text-muted">{participant.email}</p>
          </div>
          <div className="ml-auto text-right">
            <div className="text-xs uppercase tracking-wider text-brand-text-muted">Rank</div>
            <div className="text-3xl font-bold text-brand-text">#{myRank || '—'}</div>
          </div>
        </header>

        <div className="grid sm:grid-cols-3 gap-4">
          <Card>
            <span className="text-xs uppercase tracking-wider text-brand-text-muted">Points</span>
            <PointsCounter value={total} className="text-3xl font-bold text-brand-text" />
          </Card>
          <Card>
            <span className="text-xs uppercase tracking-wider text-brand-text-muted mb-1 block">Streak</span>
            <StreakFlame streak={computeStreak(activities)} />
          </Card>
          <Card>
            <span className="text-xs uppercase tracking-wider text-brand-text-muted mb-1 block">Caps this week</span>
            <CapsRing caps={caps} />
          </Card>
        </div>

        <Card>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-brand-text flex items-center gap-2">
              Points over time
              <HelpTooltip>Cumulative points since the quarter began.</HelpTooltip>
            </h3>
          </div>
          <Sparkline data={series} height={80} />
        </Card>

        <Card>
          <BadgeShelf unlockedSlugs={participant.badge_slugs || []} />
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-brand-text">Your activities</h3>
            <Link to="/log" className="text-sm text-brand-blue font-semibold hover:underline">+ Log new</Link>
          </div>
          {activities.length === 0 && (
            <p className="text-sm text-brand-text-muted">Nothing logged yet.</p>
          )}
          <ul className="divide-y divide-brand-border">
            {activities.map(a => {
              const imps = (a.post_stats || []).reduce((m, s) => Math.max(m, s?.impressions || 0), 0)
              const pending = a.publora_post_group_id && !a.post_urn
              return (
                <li key={a.id} className="py-3 flex items-center gap-3 flex-wrap sm:flex-nowrap">
                  <span className="capitalize text-sm font-medium text-brand-text w-20 shrink-0">{a.type}</span>
                  <div className="flex-1 min-w-0">
                    {a.url
                      ? <a href={a.url} target="_blank" rel="noreferrer" className="text-xs text-brand-blue truncate block hover:underline">{a.url}</a>
                      : <span className="text-xs text-brand-text-muted">{pending ? 'Publishing to LinkedIn…' : 'No URL'}</span>
                    }
                    <span className="text-xs text-brand-text-muted">{relativeTime(a.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-auto">
                    {imps > 0 && (
                      <span className="text-xs text-brand-text-muted whitespace-nowrap">
                        {imps.toLocaleString()} imps
                      </span>
                    )}
                    {pending && (
                      <span className="text-xs text-brand-blue bg-brand-blue-tint/60 px-2 py-0.5 rounded-full">pending</span>
                    )}
                    {!a.content_gate_passed && (
                      <span className="text-xs text-brand-warning bg-amber-50 px-2 py-0.5 rounded-full">Pending review</span>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        </Card>
      </div>
    </BrandShell>
  )
}

function Card({ children }) {
  return (
    <div className="bg-white border border-brand-border rounded-xl p-5 shadow-card">
      {children}
    </div>
  )
}

function Avatar({ participant }) {
  if (participant.avatar_url) {
    return <img src={participant.avatar_url} alt={participant.name} className="w-16 h-16 rounded-full object-cover" />
  }
  const initials = participant.name?.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase() || '?'
  return (
    <div className="w-16 h-16 rounded-full bg-brand-blue text-white flex items-center justify-center text-xl font-bold">
      {initials}
    </div>
  )
}

function computeStreak(activities) {
  if (!activities.length) return 0
  const days = new Set(
    activities.filter(a => a.content_gate_passed).map(a => a.created_at.slice(0, 10))
  )
  let streak = 0
  const cursor = new Date()
  while (days.has(cursor.toISOString().slice(0, 10))) {
    streak++
    cursor.setUTCDate(cursor.getUTCDate() - 1)
  }
  return streak
}
